import type { MileageToleranceSetting } from "@hakili/contracts";
import { formatDate, formatQuantity, t } from "@hakili/i18n";
import { Card, ERROR_FIELD_BORDER, ERROR_TEXT } from "@hakili/ui";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * `P1-CNW-063` EXPERTISE TOLERANCE: **Hakili's ONE mileage tolerance**, the kilometres a standing expertise report may be behind the car's
 * odometer and still be reused (founder D-1031-11, D-1101-2). The door is lane AC's: `GET` and `POST /api/v1/expertise/mileage-tolerance`,
 * both under `parametrerPaiements`, so this block is handed its doors for the administration alone and is absent for every other chair.
 *
 * **TRANSITIONAL placement.** The « Paramètres de la plateforme » screen of D-1101-2 does not exist in console-web; the block sits beside
 * the payment settings on Profil (`WF-CON-43` places the administration's parameters there) and moves with the console V2 build.
 *
 * **The screen decides no range.** It keeps the field to whole numbers and submits; a value out of the served bounds is refused by the door
 * in its own sentence, drawn verbatim where the gesture is. The bounds are SERVED and shown as a hint, never enforced here (D-1020-7: one
 * rule, one place). `unconfigured` is a served state with its own sentence, never a blank; a read the door refused is said as a refusal and
 * never drawn as « Non configurée ».
 */
export interface MileageToleranceDoors {
  readonly readTolerance: () => Promise<MileageToleranceSetting>;
  readonly publishTolerance: (input: { toleranceKm: number }) => Promise<MileageToleranceSetting>;
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

/** A served day (`YYYY-MM-DD`, a SQL date) in the catalogue's prose: « 2 novembre 2026 », the first as « 1er ». What is not a day is shown as served. */
function dayLabel(day: string): string {
  const found = /^(\d{4})-(\d{2})-(\d{2})/u.exec(day);
  if (found === null) return day;
  const instant = new Date(`${found[1] ?? ""}-${found[2] ?? ""}-${found[3] ?? ""}T12:00:00Z`);
  return Number.isNaN(instant.getTime()) ? day : formatDate(instant);
}

const kilometres = (km: number): string => fill("console.profil.toleranceValeur", { km: formatQuantity(km) });

const failureOf = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export function MileageToleranceBlock(doors: MileageToleranceDoors): React.JSX.Element {
  const [setting, setSetting] = useState<MileageToleranceSetting | null>(null);
  const [readRefusal, setReadRefusal] = useState<string | null>(null);
  /** The digits typed, no grouping: what is SENT is this whole number; what is DRAWN is the formatter's grouping of it. */
  const [digits, setDigits] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const { readTolerance } = doors;
  const seat = (served: MileageToleranceSetting): void => {
    setSetting(served);
    setDigits(served.toleranceKm === null ? "" : String(served.toleranceKm));
  };
  useEffect(() => {
    let live = true;
    void readTolerance()
      .then((served) => {
        if (live) seat(served);
      })
      .catch((failure: unknown) => {
        if (live) setReadRefusal(failureOf(failure));
      });
    return () => {
      live = false;
    };
  }, [readTolerance]);

  const inForce =
    setting?.state === "configured" &&
    setting.toleranceKm !== null &&
    setting.version !== null &&
    setting.effectiveFrom !== null
      ? {
          km: setting.toleranceKm,
          version: setting.version,
          since: setting.effectiveFrom,
          by: setting.publishedByName,
        }
      : null;

  return (
    <Card shape="console">
      <div data-testid="tolerance">
        <h2 className="text-sm font-semibold">{t("console.profil.toleranceTitre")}</h2>
        <p className="mt-0.5 mb-3 text-sm leading-relaxed text-gray-500">
          {t("console.profil.toleranceSous")}
        </p>
        {readRefusal === null ? (
          <p data-testid="tolerance-etat" className="mb-1 text-sm font-medium tabular-nums text-gray-900">
            {setting === null
              ? t("common.state.chargement")
              : inForce === null
                ? t("console.profil.toleranceNonConfiguree")
                : kilometres(inForce.km)}
          </p>
        ) : (
          <p role="alert" data-testid="tolerance-lecture-refus" className={`mb-3 text-sm ${ERROR_TEXT}`}>
            {readRefusal}
          </p>
        )}
        {inForce === null ? null : (
          <p data-testid="tolerance-version" className="mb-3 text-sm text-gray-500">
            {inForce.by === null
              ? fill("console.profil.toleranceVersionSansNom", {
                  version: String(inForce.version),
                  date: dayLabel(inForce.since),
                })
              : fill("console.profil.toleranceVersion", {
                  version: String(inForce.version),
                  date: dayLabel(inForce.since),
                  nom: inForce.by,
                })}
          </p>
        )}
        {setting === null ? null : (
          <form
            data-testid="tolerance-formulaire"
            className="mb-3 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void (async () => {
                setBusy(true);
                setRefusal(null);
                setPublished(false);
                try {
                  seat(await doors.publishTolerance({ toleranceKm: Number(digits) }));
                  setPublished(true);
                } catch (failure) {
                  setRefusal(failureOf(failure));
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            <label className="text-sm text-gray-600">
              {t("console.profil.toleranceChamp")}
              <input
                data-testid="tolerance-valeur"
                inputMode="numeric"
                // Designer R: the value is GROUPED as it is typed, by the catalogue's formatter (U+202F); the state keeps the digits and the door gets the whole number.
                value={digits === "" ? "" : formatQuantity(Number(digits))}
                onChange={(event) => {
                  const raw = event.target.value.replace(/\D/gu, "").slice(0, 12);
                  setDigits(raw === "" ? "" : String(Number(raw)));
                  setRefusal(null);
                }}
                aria-invalid={refusal === null ? undefined : true}
                aria-describedby={refusal === null ? undefined : "tolerance-refus-texte"}
                className={`mt-1 block w-full rounded-lg border px-2 py-1.5 text-xs tabular-nums ${
                  refusal === null ? "border-gray-200" : ERROR_FIELD_BORDER
                }`}
              />
            </label>
            {refusal === null ? null : (
              <p
                id="tolerance-refus-texte"
                role="alert"
                data-testid="tolerance-refus"
                className={`-mt-1 flex items-start gap-1.5 text-sm ${ERROR_TEXT}`}
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{refusal}</span>
              </p>
            )}
            <p data-testid="tolerance-bornes" className="text-xs text-gray-500">
              {fill("console.profil.toleranceBornes", {
                min: formatQuantity(setting.bounds.minKm),
                max: formatQuantity(setting.bounds.maxKm),
              })}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                data-testid="tolerance-publier"
                disabled={busy || digits === ""}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              >
                {t("console.profil.tolerancePublier")}
              </button>
              {published ? (
                <p role="status" data-testid="tolerance-publie" className="text-xs text-emerald-700">
                  {t("console.profil.tolerancePublie")}
                </p>
              ) : null}
            </div>
          </form>
        )}
        {setting === null || setting.history.length === 0 ? null : (
          <div>
            <h3 className="mb-1 text-xs font-semibold text-gray-700">
              {t("console.profil.toleranceHistorique")}
            </h3>
            <ul data-testid="tolerance-historique" className="divide-y divide-gray-50 text-sm text-gray-600">
              {setting.history.map((version) => (
                <li key={version.version} className="py-1.5 tabular-nums">
                  {version.publishedByName === null
                    ? fill("console.profil.toleranceLigneSansNom", {
                        version: String(version.version),
                        valeur: kilometres(version.toleranceKm),
                        date: dayLabel(version.effectiveFrom),
                      })
                    : fill("console.profil.toleranceLigne", {
                        version: String(version.version),
                        valeur: kilometres(version.toleranceKm),
                        date: dayLabel(version.effectiveFrom),
                        nom: version.publishedByName,
                      })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
